import React, { useCallback, useEffect, useRef, useState } from "react"
import ContactSalesModal from "components/ContactSalesModal"
import { SIGNUP_URL } from "constants/urls"
import Button from "components/ui/Button"
import Container from "components/ui/Container"
import Heading from "components/ui/Heading"
import ChatbotDemo from "components/ChatbotDemo"
import { openModal } from "utils/openModal"
import TorusCanvas from "./TorusCanvas"

const WIDGET_WIDTH = 440
const WIDGET_HEIGHT = 700

// Scripted demo conversations for the ChatbotDemo component.
// The widget auto-plays through every conversation in sequence.
const chatbotConversations = [
  {
    id: "traffic-spike",
    chipLabel: "Ask about your data",
    chipIcon: "line-chart",
    messages: [
      {
        role: "user",
        text: "Why did page views spike last week? Anything notable?",
      },
      {
        role: "bot",
        reasoning: [
          "Querying web_analytics.page_views…",
          "Pulled 482K events from Mar 22 – Apr 4",
          "Running anomaly detection on daily counts…",
          "Joining marketing_campaigns + referrer_sources…",
        ],
        text: [
          "Found it! Page views jumped from a **~26K/day baseline** to a **51K peak on Tuesday March 31**, kicking off the prior day — a **+73% week-over-week** jump.",
          "",
          "**Root cause:** Your *Data Observability* blog post was featured in a **popular industry newsletter** — driving **14,200 referrals** from an email campaign on **Mon Mar 30**.",
          "",
          "Other patterns worth noting:",
          "- **87% new visitors** on spike days (vs. 31% baseline)",
          "- Concentrated in **US, UK, Germany**",
          "- Traffic decayed back toward baseline over **the following 5 days**",
          "- `/blog/data-observability-guide` traffic is **+2,100%**",
        ].join("\n"),
        image: "/images/chatbot-demo/traffic-spike.png",
        imageAlt:
          "Preset line chart of daily page views Mar 22 – Apr 4 showing a sharp spike from ~28K/day to ~50K/day starting Mar 30",
        cta: {
          label: "Open chart in Preset",
          href: "/ai/",
        },
      },
    ],
  },
  {
    id: "churn-investigation",
    chipLabel: "Investigate trends",
    chipIcon: "exclamation",
    messages: [
      {
        role: "user",
        text: "Our MRR churn feels off this quarter. Can you investigate?",
      },
      {
        role: "bot",
        reasoning: [
          "Pulling subscriptions from crm.opportunities…",
          "Computing gross MRR churn by month…",
          "Segmenting churned accounts by tier and reason…",
          "Cross-referencing support_tickets for sentiment…",
        ],
        text: [
          "Yes — you're right to worry. **$15.4K MRR lost this quarter** across **12 accounts** — roughly **2× last quarter**.",
          "",
          "Where the damage actually came from:",
          "",
          "- **Mid-market: $7.2K** — 3 accounts. Two *Switched to competitor* ($4.8K), one cited *Missing features* ($2.4K)",
          "- **Enterprise: $5K** — a single account, acquired and consolidated onto the parent's stack",
          "- **SMB: $3.2K** — 8 accounts (most by count, least by $). 5 of 8 *Switched to competitor*; **6 of 8 churned in month 7–9**, right after annual contract end",
          "",
          "**Recommendation:** The *Missing features* exit in Mid-market is the highest-leverage signal — worth pulling those support tickets and tying them to the roadmap. For SMB, build a **renewal playbook** targeting accounts **60 days before contract end**; historical data suggests early outreach recovers ~40% of this cohort.",
        ].join("\n"),
        image: "/images/chatbot-demo/churn-breakdown.png",
        imageAlt:
          "Preset stacked horizontal bar chart of MRR lost by segment, broken down by churn reason — Mid-market is the largest band, then Enterprise, then SMB",
        cta: {
          label: "View churn dashboard",
          href: "/ai/",
        },
      },
    ],
  },
  {
    id: "sql-generation",
    chipLabel: "Generate SQL",
    chipIcon: "sql",
    messages: [
      {
        role: "user",
        text: "Write me a SQL query to find the top 10 customers by revenue in the last 90 days.",
      },
      {
        role: "bot",
        reasoning: [
          "Inspecting schema for orders and customers tables…",
          "Found `orders.total_amount` and `customers.name`…",
          "Checking for soft-delete / status columns…",
          "Optimizing with index on `orders.created_at`…",
        ],
        text: [
          "Here's an optimized query against your `public.orders` and `public.customers` tables:",
          "",
          "```sql",
          "SELECT",
          "  c.id,",
          "  c.name,",
          "  c.segment,",
          "  SUM(o.total_amount) AS revenue_90d,",
          "  COUNT(DISTINCT o.id) AS orders_90d",
          "FROM public.orders o",
          "JOIN public.customers c ON c.id = o.customer_id",
          "WHERE o.created_at >= CURRENT_DATE - INTERVAL '90 days'",
          "  AND o.status = 'completed'",
          "  AND c.deleted_at IS NULL",
          "GROUP BY c.id, c.name, c.segment",
          "ORDER BY revenue_90d DESC",
          "LIMIT 10;",
          "```",
          "",
          "A few notes on this query:",
          "- Filters to `status = 'completed'` to exclude pending/refunded orders",
          "- Excludes soft-deleted customers via `deleted_at IS NULL`",
          "- Includes `segment` so you can slice the result further",
          "- Estimated runtime: **~120ms** on the existing `orders_created_at_idx`",
        ].join("\n"),
        cta: {
          label: "Open in SQL Lab",
          href: "/ai/",
        },
      },
    ],
  },
]

function HeroSection() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [torusCenter, setTorusCenter] = useState({
    x: undefined,
    y: undefined,
  })
  const [widgetScale, setWidgetScale] = useState(1)
  // `?loop` (or `?loop=1`) replays the chatbot demo forever — handy for
  // unattended conference-screen demos where the page is just left running.
  const [loopDemo, setLoopDemo] = useState(false)
  const sectionRef = useRef(null)
  const chatbotWrapperRef = useRef(null)

  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    setLoopDemo(params.has("loop"))
  }, [])

  // Measure available space and compute a uniform scale factor for the chatbot widget.
  // The widget always renders at its native 440×700 and we use CSS `zoom` to shrink
  // it proportionally — never stretching or squishing. `zoom` (rather than
  // `transform: scale()`) re-rasterizes text/vectors at the target size, so font
  // hinting and subpixel rendering stay intact. Browser support is broad now —
  // Chrome/Safari always, Firefox since 126.
  //
  // Two layouts to handle:
  //   • lg and up: side-by-side with the text — height is constrained by the
  //     hero section height, so we scale by min(width, height).
  //   • below lg: stacked below the text — height is unconstrained (content
  //     flows), so we only scale by width.
  const updateLayout = useCallback(() => {
    if (!sectionRef.current || !chatbotWrapperRef.current) return

    const sectionRect = sectionRef.current.getBoundingClientRect()
    const wrapperParent = chatbotWrapperRef.current.parentElement
    const availableWidth = wrapperParent
      ? wrapperParent.getBoundingClientRect().width - 32
      : WIDGET_WIDTH

    const isWideLayout =
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches

    let scale
    if (isWideLayout) {
      // Side-by-side: scale by whichever dimension is tighter
      const availableHeight = sectionRect.height - 200
      scale = Math.min(
        1,
        availableHeight / WIDGET_HEIGHT,
        availableWidth / WIDGET_WIDTH
      )
    } else {
      // Stacked: scale only by width, let height flow naturally
      scale = Math.min(1, availableWidth / WIDGET_WIDTH)
    }
    setWidgetScale(scale)

    // Center torus on the chatbot widget area (only meaningful in wide layout)
    const wrapperRect = chatbotWrapperRef.current.getBoundingClientRect()
    if (wrapperRect.width === 0 || wrapperRect.height === 0) {
      setTorusCenter({ x: undefined, y: undefined })
      return
    }
    setTorusCenter({
      x: wrapperRect.left - sectionRect.left + wrapperRect.width / 2,
      y: wrapperRect.top - sectionRect.top + wrapperRect.height / 2,
    })
  }, [])

  useEffect(() => {
    updateLayout()
    const ro = new ResizeObserver(updateLayout)
    if (sectionRef.current) ro.observe(sectionRef.current)
    window.addEventListener("resize", updateLayout)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", updateLayout)
    }
  }, [updateLayout])

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen w-full overflow-hidden"
      style={{ background: "#0b0b0d" }}
    >
      <TorusCanvas
        className="absolute inset-0 h-full w-full"
        centerX={torusCenter.x}
        centerY={torusCenter.y}
      />
      <div className="relative z-10 flex min-h-screen items-center py-20 lg:py-0">
        <Container>
          <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-0">
            {/* Left: text content */}
            <div className="w-full lg:w-1/2">
              <Heading
                level="h1"
                title="Meet Preset Chatbot"
                subtitle={
                  <>
                    Take analytics to the next level with Preset. Now you can
                    use our AI Chatbot or connect your own agent to Preset MCP.
                    Build charts and dashboards. Discover new insights and their
                    underlying causes. Do more. Build faster. Empower everyone.
                  </>
                }
                size="huge"
                inverted
              />
              <div className="mt-8 flex flex-wrap gap-4">
                <Button
                  color="lush"
                  size="medium"
                  href="/book-a-demo/"
                  onClick={openModal(setIsContactModalOpen)}
                  trackClick="Book a Demo (hero)_Home"
                  nowrap
                >
                  Book a Demo
                </Button>
                <Button
                  color="white"
                  size="medium"
                  href={SIGNUP_URL}
                  trackClick="Start Free (hero)_Home"
                  nowrap
                >
                  Start Free
                </Button>
              </div>
            </div>

            {/* Right: Chatbot demo widget — stacks below text on mobile */}
            {/*
              TODO(home-hero): the `transform: scale()` wrapper below
              causes the widget (and its 3D card-flip in ChatbotDemo)
              to render slightly soft — the browser rasterizes the
              inner content at one resolution and then scales the
              bitmap, and subpixel font hinting drops on transformed
              elements. Fix is structural: render at native size and
              scale via CSS dimensions / typography instead of
              `transform: scale`. Tackle in a follow-up PR.
            */}
            <div className="flex w-full items-center justify-center lg:w-1/2">
              <div ref={chatbotWrapperRef} style={{ zoom: widgetScale }}>
                <ChatbotDemo
                  conversations={chatbotConversations}
                  loop={loopDemo}
                  onInputClick={() => setIsContactModalOpen(true)}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>

      <ContactSalesModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        sourcePage="Home - Hero"
      />
    </section>
  )
}

export default HeroSection
